import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { MainLayout } from './layouts/MainLayout';

// Pages
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import PersonnelForm from './pages/PersonnelForm';
import PersonnelList from './pages/PersonnelList';
import CategoryManagement from './pages/CategoryManagement';
import PersonnelTracking from './pages/PersonnelTracking';
import DocumentUpload from './pages/DocumentUpload';
import Timesheet from './pages/Timesheet';
import LeaveTracking from './pages/LeaveTracking';
import SettingsPage from './pages/Settings';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

// Public Route (redirects if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      
      {/* Personnel Relations */}
      <Route path="/personnel/new" element={<ProtectedRoute><PersonnelForm /></ProtectedRoute>} />
      <Route path="/personnel/:id/edit" element={<ProtectedRoute><PersonnelForm /></ProtectedRoute>} />
      <Route path="/personnel" element={<ProtectedRoute><PersonnelList /></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute><CategoryManagement /></ProtectedRoute>} />
      <Route path="/tracking" element={<ProtectedRoute><PersonnelTracking /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentUpload /></ProtectedRoute>} />
      
      {/* HR Operations */}
      <Route path="/timesheet" element={<ProtectedRoute><Timesheet /></ProtectedRoute>} />
      <Route path="/leaves" element={<ProtectedRoute><LeaveTracking /></ProtectedRoute>} />
      
      {/* Settings */}
      <Route path="/settings/personnel" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/users" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/documents" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
